import React, { createContext, useContext, useEffect, useState } from "react";
import { loadCms } from "./loadCms";
import { SITE_SETTINGS_FALLBACK } from "@/constants/cmsFallback";

/**
 * Site Settings — single source of truth for ALL business information
 * (company name, emails, phones, WhatsApp, social, address, copyright…).
 *
 * Loading strategy (resilient):
 *   1. Paint immediately from localStorage cache OR static baseline.
 *   2. Refresh from CMS with retries in the background.
 *   3. If the CMS is unreachable the site keeps rendering whatever it had.
 *
 * The CMS remains the SINGLE SOURCE OF TRUTH — the static baseline exists
 * only to survive brief outages (deploy windows, cold starts, network
 * blips). It is a snapshot of the CMS content, not an invention.
 */
const SiteSettingsCtx = createContext({ data: null, loading: true, error: null });

export function SiteSettingsProvider({ children }) {
  const [state, setState] = useState({
    data: SITE_SETTINGS_FALLBACK,
    loading: true,
    error: null,
    source: "fallback",
  });

  useEffect(() => {
    return loadCms(
      "/api/cms/api/globals/site-settings",
      "mts:site-settings:v1",
      SITE_SETTINGS_FALLBACK,
      setState
    );
  }, []);

  return (
    <SiteSettingsCtx.Provider value={state}>{children}</SiteSettingsCtx.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsCtx);
}

/** Convenience: read a single business field, empty string if unset. */
export function useSiteField(key) {
  const { data } = useSiteSettings();
  const v = data?.[key];
  return typeof v === "string" ? v : v ?? "";
}

/** Copyright helper: replaces the `{year}` token with the current year. */
export function renderCopyright(template) {
  if (!template) return "";
  return template.replace(/\{year\}/g, String(new Date().getFullYear()));
}
